/**
 * useRestoreScroll — back-nav scroll-restoration helper (v1.4 L6).
 *
 * Pairs with `use-open-user-detail.ts`. When the user drill-down's "← Back"
 * button navigates back, it passes `{ restoreScrollY }` via
 * `location.state`. The source page opts into restoration by calling this
 * hook at its top level — a single `useLayoutEffect` scrolls the viewport
 * to the captured offset before paint, so users don't see a flash-to-top.
 *
 * Caveats:
 *   - The target page must have rendered enough content that `scrollY` is
 *     achievable (otherwise the browser clamps to page bottom). In practice
 *     tabs like Engagement render placeholders while fetching so the
 *     document height is stable; if a future page shrinks after its data
 *     loads, wire a second effect that re-applies the offset on
 *     completion. For now the single synchronous scroll is sufficient.
 *   - The state key is consumed once via `navigate(pathname, { replace:
 *     true })` so a subsequent manual refresh doesn't re-scroll. We use
 *     `window.history.replaceState` instead of React Router's navigate
 *     because the latter would re-mount the page and defeat the purpose.
 */

import { useEffect, useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

import type { RestoreScrollLocationState } from './use-open-user-detail';

/**
 * Apply the scroll as early and aggressively as possible.
 *
 * Naive `useLayoutEffect` once-on-mount misbehaves on data-driven pages:
 * when the source page re-mounts, the document is still short (placeholders
 * only) so `scrollTo(500)` is clamped to `documentElement.scrollHeight - 1`
 * ≈ 0, and Chromium never re-applies the scroll once react-query finishes
 * and the list grows. To survive that, we:
 *
 *   1. Fire a synchronous scroll in `useLayoutEffect` before paint (fast
 *      path — works when the page is already tall, e.g. cached data).
 *   2. Schedule three `rAF`-spaced retries from `useEffect`. Each retry
 *      re-applies `window.scrollTo(y)` until either `window.scrollY >= y`
 *      (content now tall enough and browser accepted it) or ~300ms has
 *      elapsed. We also disable the browser's own scroll-restoration for
 *      the duration so it doesn't fight us by overwriting `scrollY` on
 *      back-nav.
 *
 * The effect still consumes `location.state` once — the retries just nudge
 * the same `restoreScrollY` value repeatedly.
 */
export function useRestoreScroll(): void {
  const location = useLocation();
  const state = (location.state ?? null) as
    | RestoreScrollLocationState
    | null;

  // Fast path — run before paint; works when content is already tall.
  useLayoutEffect(() => {
    if (!state || typeof state.restoreScrollY !== 'number') return;
    if (typeof window === 'undefined') return;
    try {
      window.scrollTo({ top: state.restoreScrollY, left: 0, behavior: 'instant' as ScrollBehavior });
    } catch {
      window.scrollTo(0, state.restoreScrollY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slow path — re-apply across several frames while the page is still
  // growing (react-query fetches complete asynchronously).
  useEffect(() => {
    if (!state || typeof state.restoreScrollY !== 'number') return;
    if (typeof window === 'undefined') return;

    const targetY = state.restoreScrollY;
    const history = window.history;
    // Opt out of the browser's own scroll restoration for the window of
    // time we're re-applying, otherwise Chromium can overwrite `scrollY`
    // mid-retry. Restore the previous mode on cleanup.
    const prevMode = history.scrollRestoration;
    try {
      history.scrollRestoration = 'manual';
    } catch {
      // Safari / older browsers may not allow toggling — tolerate silently.
    }

    const deadline = performance.now() + 320; // ~5 frames @60fps + buffer
    let rafId: number | null = null;
    const tick = (): void => {
      const now = performance.now();
      const achieved = Math.abs(window.scrollY - targetY) < 2;
      if (achieved || now >= deadline) {
        rafId = null;
        return;
      }
      try {
        window.scrollTo({ top: targetY, left: 0, behavior: 'instant' as ScrollBehavior });
      } catch {
        window.scrollTo(0, targetY);
      }
      rafId = window.requestAnimationFrame(tick);
    };
    rafId = window.requestAnimationFrame(tick);

    // Consume the state after the first frame so a manual refresh doesn't
    // re-trigger the restoration.
    try {
      const next = { ...history.state };
      if (next && typeof next === 'object' && 'usr' in next) {
        next.usr = null;
      }
      history.replaceState(next, '', window.location.href);
    } catch {
      // Non-fatal.
    }

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      try {
        history.scrollRestoration = prevMode;
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
